/**
 * AllocationPreviewModal — Human-in-the-loop preview + commit for SWM
 * hard allocation.  User can trim quantities, skip rows, multi-select
 * with bulk actions (Skip / Reset-to-BoQ), and finally hit "Commit
 * Selected" to ring-fence the corresponding `mtx_material_units`.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Loader2, X, CheckCircle2, XCircle, Filter, Layers, MoveRight } from 'lucide-react';

export default function AllocationPreviewModal({ projectId, onClose, onCommitted }) {
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [rows, setRows] = useState([]);            // proposal rows (editable)
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState({ tag: '', mCode: '', priority: '' });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/v1/projects/${projectId}/swm/allocation/hard/preview`);
        const enriched = (data.proposal || []).map((r, i) => ({
          ...r, _id: `${r.markNumber}-${r.mCode}-${r.fromBin}-${i}`,
          _skip: false, _qty: r.qty,
        }));
        setRows(enriched);
        // Auto-select all rows so the default action is "Approve All"
        setSelected(new Set(enriched.map(r => r._id)));
      } catch (e) {
        toast.error('Preview failed: ' + (e?.response?.data?.detail || e.message));
      }
      setLoading(false);
    })();
  }, [projectId]);

  const filtered = useMemo(() => rows.filter(r => {
    if (filter.tag && !(r.structuralTag || '').toLowerCase().includes(filter.tag.toLowerCase())) return false;
    if (filter.mCode && !(r.mCode || '').toLowerCase().includes(filter.mCode.toLowerCase())) return false;
    if (filter.priority && String(r.priority) !== String(filter.priority)) return false;
    return true;
  }), [rows, filter]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r._id)));
  };

  const setQty = (id, qty) => setRows(rs => rs.map(r =>
    r._id === id ? { ...r, _qty: Math.max(0, Math.min(qty, r.boqQty - r.alreadyAllocated)) } : r));

  const bulkSkip = () => setRows(rs => rs.map(r => selected.has(r._id) ? { ...r, _skip: true } : r));
  const bulkResetToBoq = () => setRows(rs => rs.map(r =>
    selected.has(r._id) ? { ...r, _qty: Math.max(0, r.boqQty - r.alreadyAllocated), _skip: false } : r));
  const bulkHalve = () => setRows(rs => rs.map(r =>
    selected.has(r._id) ? { ...r, _qty: Math.max(0, Math.round(r._qty * 0.5 * 100) / 100), _skip: false } : r));

  const totalQty = filtered.filter(r => selected.has(r._id) && !r._skip)
                            .reduce((s, r) => s + Number(r._qty || 0), 0);
  const distinctTags = new Set(filtered.filter(r => selected.has(r._id) && !r._skip).map(r => r.structuralTag)).size;
  const distinctMarks = new Set(filtered.filter(r => selected.has(r._id) && !r._skip).map(r => r.markNumber)).size;

  const doCommit = async () => {
    const linesToCommit = filtered
      .filter(r => selected.has(r._id) && !r._skip && Number(r._qty) > 0)
      .map(r => ({
        structuralTag: r.structuralTag,
        markNumber: r.markNumber,
        mCode: r.mCode,
        section: r.section,
        fromBin: r.fromBin,
        unitIds: r.unitIds,
        qty: Number(r._qty),
        priority: r.priority,
        subPriority: r.subPriority,
        boqQty: r.boqQty,
        alreadyAllocated: r.alreadyAllocated,
      }));
    if (linesToCommit.length === 0) {
      toast.error('Nothing to commit — every line is skipped or zero.'); return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/allocation/hard/commit`,
        { lines: linesToCommit, note: 'Approved via preview modal' });
      toast.success(`Committed ${data.linesCommitted} allocations · ${data.unitsReserved} units ring-fenced · ${data.marksUpdated} marks updated.`);
      onCommitted?.(data);
      onClose();
    } catch (e) {
      toast.error('Commit failed: ' + (e?.response?.data?.detail || e.message));
    }
    setCommitting(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="alloc-preview-modal">
      <div className="rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, rgba(15,23,42,.12))' }}>
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-3 border-b"
          style={{ background: 'linear-gradient(90deg, #F59206 0%, #F26B1F 100%)', color: '#fff' }}>
          <Layers className="w-5 h-5" />
          <div className="flex-1">
            <div className="text-sm font-bold">Hard Allocation · Preview & Approve</div>
            <div className="text-[10.5px] opacity-90">Ring-fence real warehouse stock against tag → mark demand. Nothing is committed until you click Approve.</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded"
            data-testid="alloc-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filters + Bulk Actions */}
        <div className="px-4 py-2 flex flex-wrap items-center gap-2 border-b text-[11px]"
          style={{ background: '#FAFAFA' }}>
          <Filter className="w-3 h-3" style={{ color: '#64748B' }} />
          <input placeholder="Filter tag…" value={filter.tag} onChange={e => setFilter({ ...filter, tag: e.target.value })}
            className="px-2 py-1 rounded border" style={{ borderColor: 'rgba(15,23,42,.12)' }}
            data-testid="alloc-filter-tag" />
          <input placeholder="Filter mCode…" value={filter.mCode} onChange={e => setFilter({ ...filter, mCode: e.target.value })}
            className="px-2 py-1 rounded border" style={{ borderColor: 'rgba(15,23,42,.12)' }}
            data-testid="alloc-filter-mcode" />
          <div className="ml-2 flex items-center gap-1">
            <span className="text-[10px]" style={{ color: '#64748B' }}>Bulk on {selected.size} selected:</span>
            <button onClick={bulkResetToBoq} className="px-2 py-0.5 rounded font-semibold text-white"
              style={{ background: '#10B981' }} data-testid="alloc-bulk-reset">Set qty = BoQ</button>
            <button onClick={bulkHalve} className="px-2 py-0.5 rounded font-semibold text-white"
              style={{ background: '#3B82F6' }} data-testid="alloc-bulk-halve">Halve qty</button>
            <button onClick={bulkSkip} className="px-2 py-0.5 rounded font-semibold text-white"
              style={{ background: '#EF4444' }} data-testid="alloc-bulk-skip">Skip</button>
          </div>
          <div className="ml-auto text-[10px]" style={{ color: '#64748B' }}>
            {filtered.length} proposal rows · <b>{selected.size}</b> selected · <b style={{ color: '#22c55e' }}>{totalQty.toLocaleString()}</b> qty · {distinctTags} tags · {distinctMarks} marks
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center gap-2 text-xs" style={{ color: '#64748B' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Generating proposal…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[12px]" style={{ color: '#64748B' }}>
              No allocation proposal — either every mark is already 100% allocated or no eligible tags exist.
            </div>
          ) : (
            <table className="w-full text-[10.5px]">
              <thead className="sticky top-0 z-10" style={{ background: '#EEF2FF', color: '#1E3A8A' }}>
                <tr>
                  <th className="p-1.5 text-left w-6">
                    <button onClick={toggleAll} title="Select all">
                      <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} readOnly
                        data-testid="alloc-select-all" />
                    </button>
                  </th>
                  <th className="p-1.5 text-left uppercase text-[9px]">Tag</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">Mark</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">mCode / Section</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">From Bin</th>
                  <th className="p-1.5 text-right uppercase text-[9px]">BoQ</th>
                  <th className="p-1.5 text-right uppercase text-[9px]">Already Alloc.</th>
                  <th className="p-1.5 text-right uppercase text-[9px]">Qty (edit)</th>
                  <th className="p-1.5 text-center uppercase text-[9px]">Priority</th>
                  <th className="p-1.5 text-center uppercase text-[9px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r._id} style={{
                    borderTop: '1px solid rgba(15,23,42,.08)',
                    background: r._skip ? '#FEE2E220' : (selected.has(r._id) ? '#F0FDF4' : '#fff'),
                  }} data-testid={`alloc-row-${r.markNumber}-${r.mCode}`}>
                    <td className="p-1.5">
                      <input type="checkbox" checked={selected.has(r._id)} onChange={() => toggle(r._id)}
                        data-testid={`alloc-checkbox-${r.markNumber}`} />
                    </td>
                    <td className="p-1.5 font-mono font-semibold" style={{ color: '#F59206' }}>{r.structuralTag}</td>
                    <td className="p-1.5 font-mono">{r.markNumber}</td>
                    <td className="p-1.5">
                      <div className="font-mono font-semibold" style={{ color: '#1E4E8C' }}>{r.mCode}</div>
                      <div className="text-[9px]" style={{ color: '#64748B' }}>{r.section}</div>
                    </td>
                    <td className="p-1.5 text-[10px]" style={{ color: '#0F766E' }}>
                      <div className="flex items-center gap-1">
                        {r.fromBin} <MoveRight className="w-2.5 h-2.5" />
                        <span style={{ color: '#F59206' }}>SS-* fab</span>
                      </div>
                      <div className="text-[9px]" style={{ color: '#94A3B8' }}>{(r.unitIds || []).length} units</div>
                    </td>
                    <td className="p-1.5 text-right font-mono">{Number(r.boqQty || 0).toLocaleString()}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: '#94A3B8' }}>{Number(r.alreadyAllocated || 0).toLocaleString()}</td>
                    <td className="p-1.5 text-right">
                      <input type="number" min={0} max={r.boqQty} step={0.1}
                        value={r._qty} onChange={e => setQty(r._id, Number(e.target.value))}
                        className="w-20 px-1 py-0.5 text-right font-mono border rounded"
                        style={{ borderColor: 'rgba(15,23,42,.15)', background: r._skip ? '#FEE2E2' : '#fff' }}
                        disabled={r._skip}
                        data-testid={`alloc-qty-${r.markNumber}`} />
                    </td>
                    <td className="p-1.5 text-center">
                      <span className="px-1.5 py-0 rounded text-[9px] font-bold"
                        style={{ background: '#F5920620', color: '#F59206' }}>
                        P{r.priority ?? '—'}·{r.subPriority ?? '—'}
                      </span>
                    </td>
                    <td className="p-1.5 text-center">
                      <button onClick={() => setRows(rs => rs.map(x => x._id === r._id ? { ...x, _skip: !x._skip } : x))}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded"
                        style={{ background: r._skip ? '#EF4444' : '#F1F5F9',
                                  color: r._skip ? '#fff' : '#64748B' }}
                        data-testid={`alloc-skip-${r.markNumber}`}>
                        {r._skip ? 'Skipped' : 'Skip'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex items-center gap-3 border-t"
          style={{ background: '#F8FAFC', borderColor: 'rgba(15,23,42,.08)' }}>
          <div className="flex items-center gap-2 text-[10.5px]" style={{ color: '#64748B' }}>
            <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
            Ring-fenced stock stays in the warehouse but is marked "Allocated for tag …" — available for issue only.
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={committing}
              className="px-3 py-1.5 rounded font-semibold text-[11px] border"
              style={{ borderColor: 'rgba(15,23,42,.15)', color: '#64748B' }}
              data-testid="alloc-cancel">
              Cancel
            </button>
            <button onClick={doCommit} disabled={committing || totalQty === 0}
              className="px-4 py-1.5 rounded font-bold text-[11px] text-white flex items-center gap-1.5 shadow"
              style={{ background: totalQty === 0 ? '#94A3B8' : '#F59206' }}
              data-testid="alloc-commit">
              {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {committing ? 'Committing…' : `Commit ${filtered.filter(r => selected.has(r._id) && !r._skip && Number(r._qty) > 0).length} Allocations`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
